import React, { useState } from "react";
import axios from "axios";
import { Input } from "src/components/input/input";
import { Card } from "src/components/ui/Card";
import HRTitleHeader from "src/components/ui/HRTitleHeader";
import { Button } from "src/components/buttons/shadcn-button";
import { uploadPhotos, getPhotos } from "src/redux/api/_hr.api";
import { submitUploadedPic } from "src/redux/api/_hr.api";
import { toast } from "react-toastify";
import { Upload, Image as ImageIcon, MapPin } from "lucide-react";
import DeletePhotos from "./Delete-photos";

const statesOfIndia = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu & Kashmir", "Ladakh"
];

const UploadForm = () => {
  const [location, setLocation] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Add refresh trigger

  // Handle file input
  const handleImage = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  // Handle submit
  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!location) {
      toast.error("Please select a location");
      return;
    }

    if (!image) {
      toast.error("Please upload an image");
      return;
    }

    try {
      setLoading(true);

      const key = await uploadPhotos(image);
      console.log("key found", key);

      await submitUploadedPic(location, key);
      
      toast.success("Photo uploaded successfully");
      
      // Trigger refresh in DeletePhotos component
      setRefreshTrigger(prev => prev + 1);

      // Reset fields
      setLocation("");
      setImage(null);
      setPreviewUrl("");
      
      // Reset file input
      const fileInput = document.getElementById("image-upload") as HTMLInputElement;
      if (fileInput) fileInput.value = "";

    } catch (error) {
      console.error(error);
      toast.error("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <HRTitleHeader
        title="Upload Photos"
        info="Upload photos of campaigns and sessions at various locations"
      />

      <div className="min-h-[70vh] w-full flex items-center justify-center p-6">
        <Card className="p-8 w-full max-w-2xl shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Form Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <Upload className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                Campaign Photo Upload
              </h2>
              <p className="text-gray-500">
                Select a location and upload a photo from your campaign
              </p>
            </div>

            {/* Location Selector */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 mr-2 text-gray-500" />
                Campaign Location
              </label>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none bg-white"
                required
              >
                <option value="">Select a state</option>
                {statesOfIndia.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <ImageIcon className="w-4 h-4 mr-2 text-gray-500" />
                Campaign Photo
              </label>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 transition-colors">
                <input
                  id="image-upload"
                  type="file"
                  onChange={handleImage}
                  className="hidden"
                />
                
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  {previewUrl ? (
                    <div className="w-full">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="max-h-64 mx-auto rounded-lg object-cover mb-4"
                      />
                      <p className="text-center text-sm text-gray-600">
                        {image?.name}
                      </p>
                      <p className="text-center text-xs text-blue-600 mt-2">
                        Click to change image
                      </p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 text-gray-400 mb-3" />
                      <p className="text-gray-600 font-medium mb-1">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-sm text-gray-500">
                        PNG, JPG or JPEG (MAX. 10MB)
                      </p>
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Upload className="w-5 h-5 mr-2" />
                  Upload Photo
                </span>
              )}
            </Button>
          </form>
        </Card>
      </div>
      <DeletePhotos refreshTrigger={refreshTrigger} />
    </>
  );
};

export default UploadForm;